import AppComponent from '../base/app-component.ts';

const CAMPI_OPZIONALI = [
  'codiceFiscale', 'partitaIva',
  'indirizzo', 'cap', 'citta', 'provincia',
  'telefono', 'cellulare', 'fax', 'email',
  'dataIscrizione', 'dataCessazione', 'dataSospensione',
  'ccnl', 'codiceConsorzio', 'codicePagamento',
  'ac', 'albo', 'numOperai', 'tipoServizio', 'codce',
] as const;

const TUTTI_I_CAMPI = ['cassa', 'codImp', 'ragioneSociale', ...CAMPI_OPZIONALI] as const;

/**
 * wc-edit-impresa — Inserimento e modifica impresa.
 *
 * Attributi:
 *   impresa-id  (opzionale) ID impresa da modificare; assente = inserimento
 *
 * Emette:
 *   impresa-salvata  { detail: { impresa, isNew } }
 */
export class EditImpresa extends AppComponent {
  static override componentName = 'wc-edit-impresa';

  protected override get templateId() { return 'edit-impresa'; }

  private editImpresaId = 0;

  protected override afterRender(): void {
    const impresaId = this.attributeNumber('impresa-id');
    if (impresaId) {
      this.editImpresaId = impresaId;
      this.loadImpresa(impresaId);
      this.setTitleModifica();
    }
    this.setupForm();
  }

  /** Reset completo per riapertura in modalità "nuovo" */
  reset(): void {
    this.editImpresaId = 0;
    this.clearForm();
    this.setTitleNuovo();
  }

  /** Carica un'impresa esistente per la modifica */
  edit(id: number): void {
    this.editImpresaId = id;
    this.clearForm();
    this.loadImpresa(id);
    this.setTitleModifica();
  }

  private clearForm(): void {
    for (const el of this.$$('input')) {
      (el as HTMLInputElement).value = '';
    }
    for (const el of this.$$('select[data-field]')) {
      (el as HTMLSelectElement).selectedIndex = 0;
    }
    this.nascondiValidationError();
  }

  private setupForm(): void {
    const form = this.$('[data-form="edit-impresa"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }
  }

  private async loadImpresa(id: number): Promise<void> {
    const result = await this.api.imprese.dettaglio(id);
    if (!result.ok) {
      this.mostraValidationError(result.error);
      return;
    }
    this.populateForm(result.data as unknown as Record<string, unknown>);
  }

  private async handleSubmit(): Promise<void> {
    const val = (sel: string) =>
      (this.$(`[data-field="${sel}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ?? '';

    const cassa = val('cassa');
    const codImp = val('codImp');
    const ragioneSociale = val('ragioneSociale');

    if (!cassa || !codImp || !ragioneSociale) {
      this.mostraValidationError('Cassa, codice impresa e ragione sociale sono obbligatori');
      return;
    }

    const dati: Record<string, string> = { cassa, codImp, ragioneSociale };
    for (const campo of CAMPI_OPZIONALI) {
      const v = val(campo);
      if (v) dati[campo] = v;
    }

    if (this.editImpresaId > 0) {
      const result = await this.api.imprese.aggiorna(this.editImpresaId, dati);
      if (!result.ok) {
        this.mostraValidationError(result.error);
        return;
      }
      Weext.toast('Impresa aggiornata', { type: 'success' });
      this.dispatch('impresa-salvata', { impresa: result.data, isNew: false });
    } else {
      const result = await this.api.imprese.crea(dati);
      if (!result.ok) {
        this.mostraValidationError(result.error);
        return;
      }
      Weext.toast('Impresa inserita', { type: 'success' });
      this.dispatch('impresa-salvata', { impresa: result.data, isNew: true });
    }
  }

  private populateForm(imp: Record<string, unknown>): void {
    for (const field of TUTTI_I_CAMPI) {
      const el = this.$(`[data-field="${field}"]`) as HTMLInputElement | HTMLSelectElement | null;
      if (el) el.value = (imp[field] as string) ?? '';
    }
  }

  private mostraValidationError(messaggio: string): void {
    const el = this.$('[data-validation-error]');
    if (!el) return;
    el.textContent = messaggio;
    el.classList.remove('d-none');
  }

  private nascondiValidationError(): void {
    const el = this.$('[data-validation-error]');
    if (!el) return;
    el.textContent = '';
    el.classList.add('d-none');
  }

  private setTitleNuovo(): void {
    const title = this.$('[data-modal-title]');
    if (title) title.innerHTML = '<i class="bi bi-building-add me-2"></i>Nuova impresa';
  }

  private setTitleModifica(): void {
    const title = this.$('[data-modal-title]');
    if (title) title.innerHTML = '<i class="bi bi-pencil me-2"></i>Modifica impresa';
  }
}
